import { RetryButton } from './RetryButton'
import { Drawer } from 'components/Drawer'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ChevronLeft } from 'blaze-ui/icons/24'
import Styles from './AIRequestDrawer.module.scss'
import { useState } from 'react'
import { clearRevisionMarks } from 'components/AIRevision/clearRevisionMarks'
import { Button } from 'blaze-components/WriterTopBar/Button'
import { useDocStore } from 'stores/DocStore'
import { observer } from 'mobx-react-lite'
import { AIRevisionDrawerContainerProps, AIRevisionDrawerProps } from './Types'
import { ChangeLength } from './SuggestedActions/ChangeLength'
import { Selection } from 'hooks/queries/layers/revisions/useCreateRevision'
import { AllSelection } from 'prosemirror-state'
import { markdownSerializer } from 'common/Prose/components/TextInlineFormatBar/components/FormatBars/markdownSerializer'
import { EditTone } from './SuggestedActions/EditTone/EditTone'
import { Translate } from './SuggestedActions/Translate'
import { useRootStore } from 'stores/RootStore'
import { AIRevisionContainer } from 'components/AIRevision/AIRevisionContainer'
import { useAlmanacEditorState } from 'stores/EditorState'

const AIRequestDrawer = observer(({ articleId, layerId, suggestedAction, ...props }: AIRevisionDrawerProps) => {
  const { drawersStore } = useRootStore()
  const state = useAlmanacEditorState()
  const [showNewRevisionScreen, setShowNewRevisionScreen] = useState(
    !suggestedAction || suggestedAction === 'ImproveQuality',
  )

  if (!state) return null

  const { view } = state

  // Fetch revision id from prose mirror marks
  let revisionId: string = ''
  view.state.doc.descendants((node) => {
    const relevantMarks = node.marks.filter((mark) => mark.type.name === 'ai_revision')
    if (relevantMarks.length > 0) {
      revisionId = relevantMarks[0].attrs.uuid
    }

    return !revisionId
  })

  let title = 'AI Suggestion'
  if (suggestedAction === 'ChangeLength') title = 'Change Length'
  if (suggestedAction === 'Translate') title = 'Translation'
  if (suggestedAction === 'EditTone') title = 'Edit Tone'
  if (suggestedAction === 'ImproveQuality') title = 'Improve Quality'

  const handleClose = () => {
    const tr = clearRevisionMarks(view.state.tr)
    view.dispatch(tr)
    drawersStore.closeDrawer()
  }

  let proseSelection = state.selection || new AllSelection(view.state.doc)
  if (proseSelection.empty) proseSelection = new AllSelection(view.state.doc)
  const doc = view.state.doc
  const documentAsMarkdown = markdownSerializer.serialize(doc)
  const selectionContent = doc.cut(proseSelection.from, proseSelection.to)
  const selectionAsMarkdown = markdownSerializer.serialize(selectionContent)

  const promptProps = {
    selection: proseSelection.toJSON() as Selection,
    documentAsMarkdown,
    selectionAsMarkdown,
  }

  const suggestedActionProps = {
    editorState: state,
    view: view,
    layerId: layerId,
    articleId: articleId,
    promptProps,
    onRevise: () => {
      setShowNewRevisionScreen(true)
    },
  }

  return (
    <Drawer.Root onClose={handleClose} {...props}>
      <Drawer.Portal>
        <Drawer.Overlay />
        <Drawer.Content fullHeight color="var(--default-bg)">
          <Drawer.InnerContent isSnappedTop>
            <Drawer.Top color="transparent">
              <div className={Styles.close}>
                <Button data-testid="drawer-close-btn" icon={ChevronLeft} onPress={handleClose} />
              </div>
              <Drawer.Title asChild>
                <Heading level={5}>{title}</Heading>
              </Drawer.Title>
              {showNewRevisionScreen && revisionId && (
                <RetryButton articleId={articleId} layerId={layerId} view={view} revisionId={revisionId} />
              )}
            </Drawer.Top>
            <Drawer.Container padding="0 10px 10px 10px">
              {showNewRevisionScreen && revisionId && (
                <AIRevisionContainer articleId={articleId} layerId={layerId} id={revisionId} />
              )}
              {!showNewRevisionScreen && !!prompt && (
                <>
                  {suggestedAction === 'ChangeLength' && <ChangeLength {...suggestedActionProps} />}
                  {suggestedAction === 'EditTone' && <EditTone {...suggestedActionProps} />}
                  {suggestedAction === 'Translate' && <Translate {...suggestedActionProps} />}
                </>
              )}
            </Drawer.Container>
          </Drawer.InnerContent>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
})

const AIRequestDrawerContainer = observer((props: AIRevisionDrawerContainerProps) => {
  const docStore = useDocStore()

  if (!docStore.layerId) {
    return null
  }

  return <AIRequestDrawer articleId={docStore.articleId} layerId={docStore.layerId} {...props} />
})

export { AIRequestDrawerContainer as AIRequestDrawer }
